import { useEffect, useState, type RefObject } from "react";

/**
 * Sledování velikosti konkrétního DOM elementu přes `ResizeObserver`.
 *
 * Po fixu (viz docs/plans/CHART-DATA-REFACTOR-FIX.md, Fáze 4):
 *   - 250ms debounce — během window resize browser fires 5-10× za vteřinu,
 *     ale finální velikost stačí (chart fetch by jinak proběhl per intermediate
 *     velikost a generoval request spam).
 *   - 50px quantize — drobné jittery ±1px nevyvolají resolution change → tile
 *     cache hit po resize do téhož 50px bucketu.
 */

const QUANTIZE_PX = 50;
const RESIZE_DEBOUNCE_MS = 250;

const quantize = (n: number): number => Math.round(n / QUANTIZE_PX) * QUANTIZE_PX;

export const useResizeObserver = (ref: RefObject<HTMLElement | null>): { width: number; height: number } => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let pending: { width: number; height: number } | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      if (!pending) return;
      const qW = quantize(pending.width);
      const qH = quantize(pending.height);
      setSize((prev) => (prev.width === qW && prev.height === qH ? prev : { width: qW, height: qH }));
      pending = null;
      timeoutId = null;
    };

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      pending = { width: entry.contentRect.width, height: entry.contentRect.height };
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(flush, RESIZE_DEBOUNCE_MS);
    });

    observer.observe(el);

    // Initial measurement synchronně (ResizeObserver fires async, první paint
    // potřebuje width hned aby chart neměl pixelWidth=0 → resolveResolution → raw
    // → fetchnul by 0 tiles, pak by se re-fetchnul při prvním resize callbacku).
    const rect = el.getBoundingClientRect();
    setSize({ width: quantize(rect.width), height: quantize(rect.height) });

    return () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [ref]);

  return size;
};
